/**
 * Composants et icones partages.
 *
 * Tout tient dans un fichier : l'app compte une vingtaine de composants
 * communs, les eclater en autant de fichiers couterait plus de navigation
 * qu'il n'en ferait gagner. La structure (entete, carte, bascule, modale) est
 * reprise de perso2 ; les composants propres a l'argent sont en fin de fichier.
 *
 * Les icones sont du SVG inline. Aucune police d'icones, aucune bibliotheque :
 * la politique de securite du site interdit les scripts tiers et l'app doit
 * fonctionner integralement hors ligne.
 */

import { useEffect, useRef, useState, type ReactNode } from 'react'
import { useNavigate } from 'react-router-dom'

import { formatMontant, formatMontantSigne, parseMontant, versSaisie } from '../domain/argent'
import { ajouterMoisKey, capitaliser, formatMois } from '../domain/temps'
import type { Categorie, Cents, Compte, Id, MoisKey, Sens, Tiers } from '../domain/types'

/** Attributs communs a tous les traces : un seul endroit pour l'epaisseur. */
const T = {
  fill: 'none',
  stroke: 'currentColor',
  strokeWidth: 1.7,
  strokeLinecap: 'round' as const,
  strokeLinejoin: 'round' as const,
}

const icone = (d: string, taille?: number) =>
  function Icone() {
    return (
      <svg viewBox="0 0 24 24" width={taille} height={taille} aria-hidden="true">
        <path {...T} d={d} />
      </svg>
    )
  }

export const IconeAccueil = icone('M3 11 12 4l9 7M5 10v10h5v-6h4v6h5V10')
export const IconeListe = icone('M8 6h12M8 12h12M8 18h12M4 6h.01M4 12h.01M4 18h.01')
export const IconeBudget = icone('M3 7a2 2 0 0 1 2-2h13v4M3 7v11a2 2 0 0 0 2 2h15V9H5a2 2 0 0 1-2-2Zm13 7h.01')
export const IconeStats = icone('M4 20V10M10 20V4M16 20v-7M22 20H2')
export const IconeMenu = icone('M4 6h16M4 12h16M4 18h16')
export const IconeChevron = icone('m9 18 6-6-6-6', 18)
export const IconeRetour = icone('M19 12H5m7-7-7 7 7 7', 22)
export const IconePlus = icone('M12 5v14M5 12h14')
export const IconeCheck = icone('m20 6-11 11-5-5')
export const IconeAlerte = icone('M12 9v4m0 4h.01M10.3 3.9 1.8 18a2 2 0 0 0 1.7 3h17a2 2 0 0 0 1.7-3L13.7 3.9a2 2 0 0 0-3.4 0Z', 18)
export const IconeGauche = icone('m15 18-6-6 6-6', 20)
export const IconeDroite = icone('m9 18 6-6-6-6', 20)
export const IconeTrombone = icone('m21 11-8.5 8.5a5 5 0 0 1-7-7L14 4a3.3 3.3 0 0 1 4.7 4.7l-8.5 8.5a1.7 1.7 0 0 1-2.4-2.4L15.5 7', 18)
export const IconeDocument = icone('M14 3H6a2 2 0 0 0-2 2v14a2 2 0 0 0 2 2h12a2 2 0 0 0 2-2V9l-6-6Zm0 0v6h6M8 13h8M8 17h5')
export const IconePoubelle = icone('M4 7h16M10 11v6m4-6v6M6 7l1 13h10l1-13M9 7V4h6v3', 18)
export const IconePartage = icone('M12 3v13m0-13-4 4m4-4 4 4M5 13v6a2 2 0 0 0 2 2h10a2 2 0 0 0 2-2v-6')

// ---------------------------------------------------------------------------
// Structure de page
// ---------------------------------------------------------------------------

export function Entete({
  titre,
  sous,
  retour,
  action,
}: {
  titre: string
  sous?: string
  retour?: boolean
  action?: ReactNode
}) {
  const navigate = useNavigate()
  return (
    <header className="entete">
      {retour && (
        <button
          type="button"
          className="btn btn--fantome btn--petit"
          onClick={() => navigate(-1)}
          aria-label="Retour"
        >
          <IconeRetour />
        </button>
      )}
      <div className="entete__titre">
        <h1>{titre}</h1>
        {sous && <span className="entete__sous">{sous}</span>}
      </div>
      {action}
    </header>
  )
}

export function Carte({
  titre,
  action,
  plate,
  children,
}: {
  titre?: string
  action?: ReactNode
  plate?: boolean
  children: ReactNode
}) {
  return (
    <section className={plate ? 'carte carte--plate' : 'carte'}>
      {(titre || action) && (
        <div className="carte__titre">
          {titre && <h2>{titre}</h2>}
          {action}
        </div>
      )}
      {children}
    </section>
  )
}

export function Encart({
  ton = 'info',
  children,
}: {
  ton?: 'info' | 'alerte' | 'danger' | 'ok'
  children: ReactNode
}) {
  return <div className={`encart encart--${ton}`}>{children}</div>
}

export function Puce({
  ton = 'neutre',
  children,
}: {
  ton?: 'neutre' | 'ok' | 'alerte' | 'info'
  children: ReactNode
}) {
  return <span className={ton === 'neutre' ? 'puce' : `puce puce--${ton}`}>{children}</span>
}

/**
 * Bascule a deux etats.
 *
 * `aria-pressed` porte l'etat et le libelle reste lisible dans les deux
 * positions : la couleur ne fait que redire ce que le texte dit deja.
 */
export function Bascule<T extends string>({
  valeur,
  options,
  onChange,
  etiquette,
}: {
  valeur: T
  options: { valeur: T; libelle: string }[]
  onChange: (v: T) => void
  etiquette?: string
}) {
  return (
    <div className="bascule" role="group" aria-label={etiquette}>
      {options.map((o) => (
        <button
          key={o.valeur}
          type="button"
          aria-pressed={valeur === o.valeur}
          onClick={() => onChange(o.valeur)}
        >
          {o.libelle}
        </button>
      ))}
    </div>
  )
}

export function Champ({
  libelle,
  aide,
  children,
}: {
  libelle: string
  aide?: string
  children: ReactNode
}) {
  return (
    <label className="champ">
      <span className="champ__libelle">{libelle}</span>
      {children}
      {aide && <span className="champ__aide">{aide}</span>}
    </label>
  )
}

// ---------------------------------------------------------------------------
// Confirmation
// ---------------------------------------------------------------------------

export function Confirmation({
  titre,
  message,
  libelleConfirmer = 'Confirmer',
  danger,
  onConfirmer,
  onAnnuler,
}: {
  titre: string
  message: string
  libelleConfirmer?: string
  danger?: boolean
  onConfirmer: () => void
  onAnnuler: () => void
}) {
  // Echap ferme la modale : sur iPhone il n'y a pas de clavier, mais l'app
  // s'ouvre aussi depuis un navigateur de bureau pendant le developpement.
  useEffect(() => {
    const surTouche = (e: KeyboardEvent) => {
      if (e.key === 'Escape') onAnnuler()
    }
    window.addEventListener('keydown', surTouche)
    return () => window.removeEventListener('keydown', surTouche)
  }, [onAnnuler])

  return (
    <div className="modale-fond" role="presentation" onClick={onAnnuler}>
      <div
        className="modale"
        role="dialog"
        aria-modal="true"
        aria-label={titre}
        onClick={(e) => e.stopPropagation()}
      >
        <div>
          <h2>{titre}</h2>
          <p className="doux" style={{ marginTop: 6 }}>
            {message}
          </p>
        </div>
        <div className="boutons">
          <button type="button" className="btn grandir" onClick={onAnnuler}>
            Annuler
          </button>
          <button
            type="button"
            className={danger ? 'btn btn--danger grandir' : 'btn btn--principal grandir'}
            onClick={onConfirmer}
          >
            {libelleConfirmer}
          </button>
        </div>
      </div>
    </div>
  )
}

// ---------------------------------------------------------------------------
// Asynchrone
// ---------------------------------------------------------------------------

export interface EtatAsync<T> {
  valeur: T | undefined
  chargement: boolean
  recharger: () => void
}

/**
 * Pour les lectures qui ne passent pas par Dexie (quota de stockage, calculs
 * ponctuels). Les donnees reactives utilisent `useLiveQuery`.
 */
export function useAsync<T>(fn: () => Promise<T>, deps: unknown[]): EtatAsync<T> {
  const [valeur, setValeur] = useState<T | undefined>(undefined)
  const [chargement, setChargement] = useState(true)
  const [tick, setTick] = useState(0)
  const fnRef = useRef(fn)
  fnRef.current = fn

  useEffect(() => {
    let vivant = true
    setChargement(true)
    fnRef
      .current()
      .then((v) => {
        if (vivant) setValeur(v)
      })
      .catch((e) => {
        console.error(e)
      })
      .finally(() => {
        if (vivant) setChargement(false)
      })
    return () => {
      vivant = false
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [...deps, tick])

  return { valeur, chargement, recharger: () => setTick((t) => t + 1) }
}

// ---------------------------------------------------------------------------
// Argent
// ---------------------------------------------------------------------------

/**
 * Affichage d'un montant. `sens` porte la direction : un montant stocke est
 * toujours positif, le signe n'existe qu'a l'ecran.
 */
export function Montant({
  valeur,
  sens,
  signe,
  className = '',
}: {
  valeur: Cents
  sens?: Sens
  /** Toujours afficher le signe, pour une variation ou un mouvement. */
  signe?: boolean
  className?: string
}) {
  const v = sens === 'depense' ? -valeur : valeur
  const texte = signe || sens ? formatMontantSigne(v) : formatMontant(v)
  const ton = sens === 'revenu' ? 'montant--revenu' : sens === 'depense' ? 'montant--depense' : v < 0 ? 'montant--negatif' : ''
  return <span className={`montant ${ton} ${className}`.trim()}>{texte}</span>
}

/**
 * Champ de saisie d'un montant en francs.
 *
 * `inputMode="decimal"` et non `type="number"` : l'input numerique d'iOS
 * refuse l'apostrophe et, selon la region du telephone, la virgule ou le point.
 * La saisie reste libre et c'est `parseMontant` qui tranche.
 */
export function ChampMontant({
  valeur,
  onChange,
  grand,
  id,
  autoFocus,
  negatif,
}: {
  valeur: Cents | null
  onChange: (c: Cents | null) => void
  grand?: boolean
  id?: string
  autoFocus?: boolean
  /** Autorise un montant negatif : seul un solde de compte peut l'etre. */
  negatif?: boolean
}) {
  const [texte, setTexte] = useState(valeur === null ? '' : versSaisie(valeur))
  const dernier = useRef(valeur)

  // Resynchronise si la valeur change de l'exterieur (application d'un modele).
  useEffect(() => {
    if (valeur !== dernier.current) {
      dernier.current = valeur
      setTexte(valeur === null ? '' : versSaisie(valeur))
    }
  }, [valeur])

  const invalide = texte.trim() !== '' && parseMontant(texte) === null
  return (
    <input
      id={id}
      type="text"
      inputMode="decimal"
      autoComplete="off"
      autoFocus={autoFocus}
      placeholder="0.00"
      className={grand ? 'saisie-montant' : undefined}
      aria-invalid={invalide}
      value={texte}
      onChange={(e) => {
        setTexte(e.target.value)
        const c = parseMontant(e.target.value)
        dernier.current = c
        onChange(c !== null && c < 0 && !negatif ? null : c)
      }}
    />
  )
}

export function SelectCompte({
  comptes,
  valeur,
  onChange,
  tous,
}: {
  comptes: Compte[]
  valeur: Id | null
  onChange: (id: Id | null) => void
  tous?: string
}) {
  return (
    <select value={valeur ?? ''} onChange={(e) => onChange(e.target.value || null)}>
      {tous !== undefined && <option value="">{tous}</option>}
      {comptes
        .filter((c) => !c.archive || c.id === valeur)
        .map((c) => (
          <option key={c.id} value={c.id}>
            {c.nom}
          </option>
        ))}
    </select>
  )
}

/** Categories groupees sous leur parente, filtrees par sens. */
export function SelectCategorie({
  categories,
  sens,
  valeur,
  onChange,
  toutes,
  racinesSeules,
}: {
  categories: Categorie[]
  sens?: Sens
  valeur: Id | null
  onChange: (id: Id | null) => void
  toutes?: string
  racinesSeules?: boolean
}) {
  const visibles = categories.filter((c) => (!sens || c.sens === sens) && (!c.archive || c.id === valeur))
  const racines = visibles.filter((c) => !c.parentId || !visibles.some((p) => p.id === c.parentId))
  return (
    <select value={valeur ?? ''} onChange={(e) => onChange(e.target.value || null)}>
      {toutes !== undefined ? <option value="">{toutes}</option> : valeur === null && <option value="">Choisir…</option>}
      {racines.map((r) => {
        const enfants = racinesSeules ? [] : visibles.filter((c) => c.parentId === r.id)
        return enfants.length ? (
          <optgroup key={r.id} label={r.nom}>
            <option value={r.id}>{r.nom}</option>
            {enfants.map((e) => (
              <option key={e.id} value={e.id}>
                {r.nom} › {e.nom}
              </option>
            ))}
          </optgroup>
        ) : (
          <option key={r.id} value={r.id}>
            {r.nom}
          </option>
        )
      })}
    </select>
  )
}

export function SelectTiers({
  tiers,
  valeur,
  onChange,
  sansMoi,
  vide,
}: {
  tiers: Tiers[]
  valeur: Id | null
  onChange: (id: Id | null) => void
  sansMoi?: boolean
  vide?: string
}) {
  return (
    <select value={valeur ?? ''} onChange={(e) => onChange(e.target.value || null)}>
      {vide !== undefined && <option value="">{vide}</option>}
      {tiers
        .filter((t) => (!sansMoi || t.type !== 'moi') && (!t.archive || t.id === valeur))
        .sort((a, b) => (a.type === 'moi' ? -1 : b.type === 'moi' ? 1 : a.nom.localeCompare(b.nom, 'fr')))
        .map((t) => (
          <option key={t.id} value={t.id}>
            {t.nom}
          </option>
        ))}
    </select>
  )
}

export function NavMois({ mois, onChange }: { mois: MoisKey; onChange: (m: MoisKey) => void }) {
  return (
    <div className="nav-mois">
      <button type="button" className="btn btn--petit" aria-label="Mois précédent" onClick={() => onChange(ajouterMoisKey(mois, -1))}>
        <IconeGauche />
      </button>
      <span className="nav-mois__titre">{capitaliser(formatMois(mois))}</span>
      <button type="button" className="btn btn--petit" aria-label="Mois suivant" onClick={() => onChange(ajouterMoisKey(mois, 1))}>
        <IconeDroite />
      </button>
    </div>
  )
}

/**
 * Jauge de consommation, avec le repere du temps ecoule.
 *
 * Le trait vertical est ce qui rend la jauge utile en cours de mois : 60 %
 * consomme n'inquiete pas le 25, beaucoup plus le 8.
 */
export function Jauge({ taux, ecoule, etat }: { taux: number; ecoule?: number; etat?: 'ok' | 'attention' | 'depasse' }) {
  const largeur = Math.min(1, Math.max(0, Number.isFinite(taux) ? taux : 1))
  const classe = etat === 'depasse' ? 'jauge__part jauge__part--depasse' : etat === 'attention' ? 'jauge__part jauge__part--alerte' : 'jauge__part'
  return (
    <div className="jauge" role="presentation">
      <div className={classe} style={{ width: `${largeur * 100}%` }} />
      {ecoule !== undefined && ecoule > 0 && ecoule < 1 && <div className="jauge__repere" style={{ left: `${ecoule * 100}%` }} />}
    </div>
  )
}

export function Pastille({ couleur }: { couleur: string }) {
  return <span className="pastille" style={{ background: couleur }} aria-hidden="true" />
}

/**
 * Remet un fichier a l'utilisateur.
 *
 * Sur iPhone, la feuille de partage est la bonne porte de sortie : elle permet
 * d'envoyer le PDF par courriel ou messagerie, ou de l'enregistrer dans
 * Fichiers. En mode standalone, un `<a download>` echoue souvent en silence ;
 * on n'y recourt qu'a defaut, avec l'ouverture dans un onglet en dernier repli.
 */
export async function remettreFichier(blob: Blob, nom: string, preferePartage = true): Promise<'partage' | 'telecharge' | 'ouvert' | 'annule'> {
  const fichier = new File([blob], nom, { type: blob.type })
  const nav = navigator as Navigator & { canShare?: (d: ShareData) => boolean }
  if (preferePartage && nav.share && nav.canShare?.({ files: [fichier] })) {
    try {
      await nav.share({ files: [fichier], title: nom })
      return 'partage'
    } catch (e) {
      if ((e as DOMException).name === 'AbortError') return 'annule'
    }
  }
  const url = URL.createObjectURL(blob)
  const standaloneIOS = (navigator as Navigator & { standalone?: boolean }).standalone === true
  if (standaloneIOS) {
    window.open(url, '_blank')
    setTimeout(() => URL.revokeObjectURL(url), 60_000)
    return 'ouvert'
  }
  const a = document.createElement('a')
  a.href = url
  a.download = nom
  document.body.appendChild(a)
  a.click()
  a.remove()
  setTimeout(() => URL.revokeObjectURL(url), 60_000)
  return 'telecharge'
}

/** Execute une action asynchrone en capturant son message d'erreur. */
export function useAction() {
  const [erreur, setErreur] = useState<string | null>(null)
  const [enCours, setEnCours] = useState(false)
  const executer = async <T,>(fn: () => Promise<T>): Promise<T | undefined> => {
    setErreur(null)
    setEnCours(true)
    try {
      return await fn()
    } catch (e) {
      setErreur(e instanceof Error ? e.message : String(e))
      return undefined
    } finally {
      setEnCours(false)
    }
  }
  return { erreur, enCours, executer, setErreur }
}
